import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Upload, X, FileText, CheckCircle, AlertCircle, Folder, 
  ArrowRight, Download, Trash2, Eye, Plus
} from 'lucide-react';
import { formsAPI } from '../../lib/api.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import { hasCapability } from '../../lib/rbac.js';

function BulkUploadForms({ onClose }) {
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedCategory, setSelectedCategory] = useState({
    categoryId: '',
    subcategoryId: '',
    subSubcategoryId: ''
  });
  const [autoCategorize, setAutoCategorize] = useState(true);

  const queryClient = useQueryClient();

  // Fetch categories for selection
  const { data: categories } = useQuery({
    queryKey: ['forms-categories-tree'],
    queryFn: () => formsAPI.whatsapp.getCategoryTree({ language: 'en' }),
  });

  const uploadMutation = useMutation({
    mutationFn: async (uploadData) => {
      const results = [];
      for (const fileData of uploadData) {
        try {
          const formData = new FormData();
          formData.append('title', fileData.title);
          formData.append('category', fileData.category);
          formData.append('language', fileData.language);
          formData.append('isPublic', fileData.isPublic.toString());
          if (fileData.categoryId) formData.append('categoryId', fileData.categoryId);
          if (fileData.subcategoryId) formData.append('subcategoryId', fileData.subcategoryId);
          if (fileData.subSubcategoryId) formData.append('subSubcategoryId', fileData.subSubcategoryId);
          formData.append('file', fileData.file);

          const result = await formsAPI.create(formData);
          results.push({ success: true, file: fileData.fileName, result });
        } catch (error) {
          results.push({ success: false, file: fileData.fileName, error: error.message });
        }
      }
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['forms']);
      queryClient.invalidateQueries(['forms-categories-tree']);
      alert('Bulk upload completed!');
    },
    onError: (error) => {
      alert('Bulk upload failed: ' + error.message);
    }
  });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    const pdfFiles = droppedFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== droppedFiles.length) {
      alert('Only PDF files are allowed');
    }
    
    addFiles(pdfFiles);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const pdfFiles = selectedFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== selectedFiles.length) {
      alert('Only PDF files are allowed');
    }
    
    addFiles(pdfFiles);
  };

  const addFiles = (newFiles) => {
    const filesWithMetadata = newFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      fileName: file.name,
      title: file.name.replace('.pdf', ''),
      category: '',
      language: 'en',
      isPublic: true,
      categoryId: selectedCategory.categoryId,
      subcategoryId: selectedCategory.subcategoryId,
      subSubcategoryId: selectedCategory.subSubcategoryId,
      status: 'pending'
    }));
    
    setFiles(prev => [...prev, ...filesWithMetadata]);
  };

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const updateFileMetadata = (fileId, field, value) => {
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, [field]: value } : f
    ));
  };

  const autoCategorizeFiles = () => {
    setFiles(prev => prev.map(file => {
      const fileName = file.fileName.toLowerCase();
      let category = '';
      let categoryId = '';
      let subcategoryId = '';
      let subSubcategoryId = '';

      // Auto-categorize based on filename patterns
      if (fileName.includes('gpf') || fileName.includes('provident')) {
        category = 'GPF';
        const gpfCategory = categories?.data?.data?.find(cat => cat.slug === 'gpf');
        if (gpfCategory) {
          categoryId = gpfCategory.id;
          if (fileName.includes('refund')) {
            const refundSub = gpfCategory.subcategories?.find(sub => sub.slug === 'refundable');
            if (refundSub) subcategoryId = refundSub.id;
          } else if (fileName.includes('non') || fileName.includes('na')) {
            const nonRefundSub = gpfCategory.subcategories?.find(sub => sub.slug === 'non-refundable');
            if (nonRefundSub) subcategoryId = nonRefundSub.id;
          }
        }
      } else if (fileName.includes('medical') || fileName.includes('a form') || fileName.includes('cd form')) {
        category = 'Medical bills';
        const medicalCategory = categories?.data?.data?.find(cat => cat.slug === 'medical-bills');
        if (medicalCategory) {
          categoryId = medicalCategory.id;
          if (fileName.includes('a form')) {
            const aFormSub = medicalCategory.subcategories?.find(sub => sub.slug === 'a-form');
            if (aFormSub) subcategoryId = aFormSub.id;
          } else if (fileName.includes('cd form')) {
            const cdFormSub = medicalCategory.subcategories?.find(sub => sub.slug === 'cd-form');
            if (cdFormSub) subcategoryId = cdFormSub.id;
          }
        }
      } else if (fileName.includes('ltc') || fileName.includes('travel')) {
        category = 'LTC';
        const ltcCategory = categories?.data?.data?.find(cat => cat.slug === 'ltc');
        if (ltcCategory) {
          categoryId = ltcCategory.id;
          if (fileName.includes('maharashtra') || fileName.includes('darshan')) {
            const maharashtraSub = ltcCategory.subcategories?.find(sub => sub.slug === 'maharashtra-darshan');
            if (maharashtraSub) subcategoryId = maharashtraSub.id;
          } else if (fileName.includes('swagram')) {
            const swagramSub = ltcCategory.subcategories?.find(sub => sub.slug === 'swagram');
            if (swagramSub) subcategoryId = swagramSub.id;
          }
        }
      } else if (fileName.includes('agrim') || fileName.includes('advance')) {
        category = 'Government agrim';
        const agrimCategory = categories?.data?.data?.find(cat => cat.slug === 'government-agrim');
        if (agrimCategory) {
          categoryId = agrimCategory.id;
          if (fileName.includes('computer') || fileName.includes('संगणक')) {
            const computerSub = agrimCategory.subcategories?.find(sub => sub.slug === 'computer-agrim');
            if (computerSub) subcategoryId = computerSub.id;
          } else if (fileName.includes('home') || fileName.includes('गृह')) {
            const homeSub = agrimCategory.subcategories?.find(sub => sub.slug === 'home-loan-agrim');
            if (homeSub) subcategoryId = homeSub.id;
          } else if (fileName.includes('motorcycle') || fileName.includes('मोटार')) {
            const motorcycleSub = agrimCategory.subcategories?.find(sub => sub.slug === 'motorcycle-agrim');
            if (motorcycleSub) subcategoryId = motorcycleSub.id;
          }
        }
      } else if (fileName.includes('circular') || fileName.includes('paripatrak')) {
        category = 'Paripatrak';
        const paripatrakCategory = categories?.data?.data?.find(cat => cat.slug === 'paripatrak');
        if (paripatrakCategory) {
          categoryId = paripatrakCategory.id;
          const oldSub = paripatrakCategory.subcategories?.find(sub => sub.slug === 'old');
          if (oldSub) {
            subcategoryId = oldSub.id;
            const year2025Sub = oldSub.subSubcategories?.find(subSub => subSub.slug === '2025');
            if (year2025Sub) subSubcategoryId = year2025Sub.id;
          }
        }
      } else if (fileName.includes('welfare') || fileName.includes('anudan') || fileName.includes('yojana')) {
        category = 'Welfare schemes';
        const welfareCategory = categories?.data?.data?.find(cat => cat.slug === 'welfare-schemes');
        if (welfareCategory) {
          categoryId = welfareCategory.id;
          // Map specific welfare schemes
          if (fileName.includes('daptar')) {
            const daptarSub = welfareCategory.subcategories?.find(sub => sub.slug === 'daptar-anudan');
            if (daptarSub) subcategoryId = daptarSub.id;
          } else if (fileName.includes('eklavya')) {
            const eklavyaSub = welfareCategory.subcategories?.find(sub => sub.slug === 'eklavy-yojana');
            if (eklavyaSub) subcategoryId = eklavyaSub.id;
          } else if (fileName.includes('pustak')) {
            const pustakSub = welfareCategory.subcategories?.find(sub => sub.slug === 'pustak-anudan');
            if (pustakSub) subcategoryId = pustakSub.id;
          } else if (fileName.includes('shishvrutti')) {
            const shishvruttiSub = welfareCategory.subcategories?.find(sub => sub.slug === 'shishvrutti-anudan');
            if (shishvruttiSub) subcategoryId = shishvruttiSub.id;
          }
        }
      }

      return {
        ...file,
        category,
        categoryId,
        subcategoryId,
        subSubcategoryId
      };
    }));
  };

  const handleBulkUpload = async () => {
    if (files.length === 0) {
      alert('Please select files to upload');
      return;
    }

    setUploading(true);
    try {
      await uploadMutation.mutateAsync(files);
      setFiles([]);
      onClose();
    } catch (error) {
      console.error('Bulk upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const getCategoryPath = (file) => {
    if (!file.categoryId) return file.category || 'Uncategorized';
    
    const category = categories?.data?.data?.find(cat => cat.id === parseInt(file.categoryId));
    if (!category) return file.category || 'Uncategorized';
    
    let path = category.name;
    
    if (file.subcategoryId) {
      const subcategory = category.subcategories?.find(sub => sub.id === parseInt(file.subcategoryId));
      if (subcategory) {
        path += ` > ${subcategory.name}`;
        
        if (file.subSubcategoryId) {
          const subSubcategory = subcategory.subSubcategories?.find(subSub => subSub.id === parseInt(file.subSubcategoryId));
          if (subSubcategory) {
            path += ` > ${subSubcategory.name}`;
          }
        }
      }
    }
    
    return path;
  };

  if (!hasCapability('canManageForms')) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Access Denied</h3>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          You don't have permission to upload forms.
        </p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Bulk Upload Forms</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Upload multiple forms at once with automatic categorization
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-2"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upload Area */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Files</h3>
              
              {/* Drag and Drop Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                  dragActive
                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                
                <div className="space-y-4">
                  <div className="flex items-center justify-center">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-200 ${
                      dragActive
                        ? 'bg-blue-100 dark:bg-blue-900/30'
                        : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <Upload className={`w-8 h-8 transition-colors duration-200 ${
                        dragActive
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-400'
                      }`} />
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {dragActive ? 'Drop your files here' : 'Upload PDF Forms'}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Drag and drop PDF files here, or click to browse
                    </p>
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <FileText className="w-4 h-4" />
                      <span>PDF files only (max 10MB each)</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Auto-categorization toggle */}
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <input
                  type="checkbox"
                  id="autoCategorize"
                  checked={autoCategorize}
                  onChange={(e) => setAutoCategorize(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="autoCategorize" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enable automatic categorization based on filename
                </label>
              </div>

              {/* Auto-categorize button */}
              {files.length > 0 && autoCategorize && (
                <Button
                  onClick={autoCategorizeFiles}
                  className="w-full"
                  variant="outline"
                >
                  <Folder className="w-4 h-4 mr-2" />
                  Auto-categorize Files
                </Button>
              )}
            </div>

            {/* Files List */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Files ({files.length})
                </h3>
                {files.length > 0 && (
                  <Button
                    onClick={() => setFiles([])}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {files.map((file) => (
                  <div key={file.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-gray-900 dark:text-white truncate">
                            {file.fileName}
                          </h4>
                          <Button
                            onClick={() => removeFile(file.id)}
                            variant="ghost"
                            size="sm"
                            className="p-1 h-6 w-6 text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={file.title}
                            onChange={(e) => updateFileMetadata(file.id, 'title', e.target.value)}
                            placeholder="Form title"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                          
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Category: {getCategoryPath(file)}
                          </div>
                          
                          <div className="flex gap-2">
                            <select
                              value={file.language}
                              onChange={(e) => updateFileMetadata(file.id, 'language', e.target.value)}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            >
                              <option value="en">English</option>
                              <option value="mr">Marathi</option>
                            </select>
                            
                            <select
                              value={file.isPublic ? 'true' : 'false'}
                              onChange={(e) => updateFileMetadata(file.id, 'isPublic', e.target.value === 'true')}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            >
                              <option value="true">Public</option>
                              <option value="false">Private</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-4 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkUpload}
            disabled={files.length === 0 || uploading}
            className="flex items-center gap-2"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload {files.length} Files
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default BulkUploadForms;
